/* Fund Overview — "where am I?" (locked 2026-08-31, mock-fund-tabs;
 * polish 2026-09-03 after Ale watched the reel).
 *
 * The chain graph (every box a door into the Record tab — no YOURS
 * badge on the fund's own page), the fund's description as PROSE with
 * its figures as data (sentences, not labels), and the chain-status
 * block as sentences with per-level lines. Chain bottoms say how they
 * end (mock-lookthrough §3): an open walk is a door to its card, an
 * elected stop names its reason, a by-rule end speaks plainly.
 *
 * The drawing's canon (Ale 2026-09-03):
 *   · every line wears the colour of the OBJECT it stands for — the
 *     holder → instrument leg is the POSITION (amber), the instrument →
 *     issuer leg is the INSTRUMENT (blue); arrowheads too;
 *   · horizontal bars are LINKED to the verticals with a curved joint
 *     of the boxes' own corner radius — nothing floats;
 *   · the instrument → issuer wire is SHORT (the issuer sits close under
 *     its paper); the holder → instrument wire is the long one;
 *   · boxes are full RINGS on a light tint of their own colour: entity a
 *     thick navy ring, instrument a thin blue ring with blue text; the
 *     position is not a box — its figures sit beside the amber wire on a
 *     light amber shade;
 *   · the instrument's issued balance is a small second line INSIDE its
 *     box, never a currency; an instrument trade (an issuance, a
 *     buy-back — no position) is a blue chip on the instrument leg, the
 *     way position trades are amber chips on the position leg;
 *   · a commitment package (commitment + undrawn records) is ONE position
 *     with one figure block: "EUR 25m committed · 3m drawn" (Ale, 3 Nov 2026).
 * SYMBOL-FREE beyond that: no dots, no legend; no explanatory note on
 * the surface — the why lives in the help-dots. */

import { Fragment, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import type { DrawerTarget } from '../ObjectDrawer'
import type { RecordTablePayload, RtSegment } from '../RecordTable'
import type { AsOfView, Lens, MatrixRow, WalkNode } from './fundArea'
import { chainStatus, showParts } from './chainStatus'
import { balanceSourcesOf, type DroppedLeg } from './asOf'
import { formatOfficerDate } from '../../lib/officerFormat'
import {
  arrowClass,
  chainOf,
  diagramTree,
  fanJoins,
  instrumentTradesOf,
  mergeJoins,
  shortNames,
  straightClass,
  type Join,
  type TreeChild,
  type TreeNode,
  type WireEnd,
} from './chainTree'
import { hasCoOwnership } from './chainTree'
import { isWideChain } from './coownedLayout'
import ChainCanvas from './ChainCanvas'
import FundSection from '../navigation/FundSection'
import { sectionObjectIds, segmentOnSection, verticalSection, type Focus } from './focus'
import {
  feederChainStatus,
  feederItself,
  mastersOnChain,
  mastersTotal,
  type MasterLink,
} from './masters'
import { fmt, positionFigures } from './overviewFigures'
import { overviewStructureFacts } from './overviewFacts'
import CoOwnedChain from './CoOwnedChain'
import WalkChip from './WalkChip'
import FocusChip from './FocusChip'
import Sentence, { SentenceParts } from '../Sentence'
import { compileSentence } from '../../lib/sentences/compile'
import { HelpDot } from '../guidance/Guidance'
import { flatEvidence, optionalDemands } from './evidenceRows'
import { OptionalChip, OptionalList } from '../OptionalEvidence'
import { breaksWords, type FundLedger } from '../ledgerWords'
import { RECORD_START_RULE, recordStartPayload } from './recordStart'

interface Props {
  summaryTarget?: HTMLElement | null
  contextual?: boolean
  chainAttention?: string | null
  /** The fund this page is of — every door into the workbench carries it. */
  fundId?: string
  table: RecordTablePayload
  matrix: MatrixRow[]
  walk: WalkNode[]
  /** The master funds this feeder holds (16a/16c). Empty for an ordinary
   *  fund — the backend serves them from /api/funds/{id}/masters (a
   *  follow-up; see BUILD.md). A master renders collapsed behind a door and
   *  is counted IN FULL in the chain status. */
  masters?: MasterLink[]
  /** The focus model (Ale 2026-09-02): a box click opens the drawer on
   *  that object and lights its vertical section; everything else dims. */
  focus: Focus | null
  /** The active rebuild lens (Ale 14b): when set, the diagram, prose and
   *  the per-level parts are computed over the rebuilt payload; the
   *  caption says which record they are of. The HEADLINE never follows
   *  the lens — it is the ledger's, on the full record. */
  lens?: Lens | null
  /** The fund's ledger (B1, Ale 2026-09-04): the chain-status headline
   *  prints ITS proven / demanded / owed and its standing breaks. */
  ledger?: FundLedger | null
  /** The record AS OF A DATE (R3b): set when the table is the engine's D
   *  view. Carries the holdings the view left out — drawn greyed IN PLACE
   *  ("no balance as of 31 May 2026 · first evidence 30 Jun 2026"), never
   *  vanished — the reader flags, and the door back to today. */
  asOf?: AsOfView | null
  onOpen: (target: DrawerTarget) => void
  onClearFocus: () => void
  /** The trade chips stay doors into the Record tab — with the focus set. */
  onJump: (target: DrawerTarget) => void
  /** The fund facts block (Ale 2026-09-04): the RECORD START — the date the
   *  record of this fund begins; nothing dated before it is demanded or
   *  chased. A manager sets it inline; an officer reads it. */
  fund?: { record_start_date?: string | null } | null
  canSetRecordStart?: boolean
  onSetRecordStart?: (iso: string | null) => Promise<void>
}

/* The fund facts block — one row per fact: LABEL · value › door. Today the
 * record start alone (Ale 2026-09-04); the contact line joins it with the
 * header rework. The inline date field is the house one (fa-rbinput). */
function FundFacts({
  fund,
  canSet,
  onSet,
}: {
  fund: { record_start_date?: string | null } | null | undefined
  canSet: boolean
  onSet?: (iso: string | null) => Promise<void>
}) {
  const stored = fund?.record_start_date ?? null
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const open = () => {
    setDraft(stored ?? '')
    setFailed(false)
    setEditing(true)
  }
  const save = async () => {
    if (!onSet || busy) return
    const payload = recordStartPayload(draft)
    if (draft.trim() !== '' && payload === null) return
    setBusy(true)
    setFailed(false)
    try {
      await onSet(payload)
      setEditing(false)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }
  const editable = canSet && !!onSet
  return (
    <div className="fa-card" data-testid="fund-facts">
      <div className="fa-chd">
        Fund facts
        <span className="right">
          <HelpDot k="fund.record_start" />
        </span>
      </div>
      <div className="fa-fact" data-testid="fund-fact-record-start">
        <span className="fa-factk">Record start</span>
        <span className="fa-factv">
          {editing ? (
            <span className="fa-factform" data-testid="record-start-form">
              <input
                type="date"
                className="fa-rbinput"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void save()
                  if (e.key === 'Escape') setEditing(false)
                }}
                disabled={busy}
                data-testid="record-start-input"
                aria-label="the date the record of this fund begins"
                autoFocus
              />
              <button
                type="button"
                className="fa-rbpreset"
                onClick={() => void save()}
                disabled={busy}
                data-testid="record-start-save"
              >
                {busy ? 'saving…' : draft.trim() === '' ? 'clear' : 'save'}
              </button>
              <button
                type="button"
                className="fa-factdoor"
                onClick={() => setEditing(false)}
                disabled={busy}
                data-testid="record-start-cancel"
              >
                cancel
              </button>
              {failed ? (
                <span className="fa-factfail" data-testid="record-start-failed">
                  not saved — try again
                </span>
              ) : null}
            </span>
          ) : stored ? (
            <>
              <b className="sn-datum" data-testid="record-start-value">
                {formatOfficerDate(stored) ?? stored}
              </b>{' '}
              — {RECORD_START_RULE}
              {editable ? (
                <>
                  {' '}
                  ›{' '}
                  <button
                    type="button"
                    className="fa-factdoor"
                    onClick={open}
                    data-testid="record-start-change"
                  >
                    change it
                  </button>
                </>
              ) : null}
            </>
          ) : (
            <>
              <span data-testid="record-start-value">not yet set</span>
              {editable ? (
                <>
                  {' '}
                  ›{' '}
                  <button
                    type="button"
                    className="fa-factdoor"
                    onClick={open}
                    data-testid="record-start-set"
                  >
                    set it
                  </button>
                </>
              ) : (
                <> — everything is demanded until a manager sets it</>
              )}
            </>
          )}
        </span>
      </div>
    </div>
  )
}

/** What fixed a leg's figure in the D view, as its date line says: a
 *  statement ("as of 31 May 2026") or the movements alone. */
function sourceLine(s: RtSegment): string | null {
  const date = formatOfficerDate(s.as_of)
  if (s.balance_source === 'trades') return date ? `from movements · ${date}` : 'from movements'
  return date ? `as of ${date}` : null
}

/** Names in a clause: "A", "A and B", "A, B and C". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/* No pills on a box (Ale 2026-09-03): proven is the default state, the
 * chain-status block says how much is proven, and gaps live there as
 * doors — a badge on a box only competes with the focus highlight. */

type Lit = 'on' | 'dim' | undefined
const focusCls = (state: Lit) => (state === 'on' ? ' focus' : state === 'dim' ? ' dim' : '')

/* ── The wires ────────────────────────────────────────────────────────
 * One WIRE is a vertical run the full width of its column, drawn in two
 * halves so each end can be a straight line, a curved corner into a bar
 * it shares with its siblings (first / last), or a T through the bar
 * (mid). The bar itself is the sum of the halves' top or bottom borders
 * — there is no separate bar element to float. A straight half that
 * meets a `last` corner at the seam is right-anchored like the corner
 * (`r`, chainTree straightClass) so both snap to the same pixel. */
type End = WireEnd

function Half({ end, side, other }: { end: End; side: 'top' | 'bottom'; other: End }) {
  if (end === 'first' || end === 'last')
    return <i className={`jt ${side} ${end}`} data-join={end} />
  const straight = straightClass(side, end, other) ?? `jt ${side} v`
  if (end === 'mid')
    return (
      <>
        <i className={`jt ${side} bar`} data-join="mid" />
        <i className={straight} />
      </>
    )
  return <i className={straight} />
}

function Wire({
  kind,
  top,
  bottom,
  arrow,
  height,
  lit,
  ghost,
  testid,
  children,
}: {
  kind: 'pos' | 'ins'
  top: End
  bottom: End
  /** An arrowhead at the bottom, into the box below. */
  arrow?: boolean
  height?: number
  lit: Lit
  /** A leg with no balance as of the date: greyed, dashed (R3b). */
  ghost?: boolean
  testid?: string
  children?: React.ReactNode
}) {
  return (
    <div
      className={`fa-edge ${kind}${ghost ? ' ghost' : ''}${focusCls(lit)}`}
      data-testid={testid}
      data-kind={kind === 'pos' ? 'position' : 'instrument'}
      data-focus={lit}
      data-ghost={ghost ? true : undefined}
      style={height ? { height } : undefined}
    >
      <Half end={top} side="top" other={bottom} />
      <Half end={bottom} side="bottom" other={top} />
      {arrow ? <i className={arrowClass(top)} data-arrow={kind} /> : null}
      {children}
    </div>
  )
}

/** A short straight stub between a box and a bar: from the holder down to
 *  the fan (amber, no arrowhead), or from the merge bar into the issuer
 *  (blue, arrowhead). */
function Stub({
  kind,
  arrow,
  lit,
  ghost,
  testid,
}: {
  kind: 'pos' | 'ins'
  arrow?: boolean
  lit: Lit
  ghost?: boolean
  testid?: string
}) {
  return (
    <div
      className={`fa-stub ${kind}${ghost ? ' ghost' : ''}${focusCls(lit)}`}
      data-testid={testid}
      data-kind={kind === 'pos' ? 'position' : 'instrument'}
      data-focus={lit}
    >
      <i className="jt v" />
      {arrow ? <i className="ah" data-arrow={kind} /> : null}
    </div>
  )
}

const POS_H = 64 // the long leg: holder → instrument
const INS_H = 28 // the short leg: instrument → issuer, close under its paper
const MERGE_H = 16 // the stub under a merge bar into the issuer

export default function FundOverviewTab({
  fundId,
  table,
  matrix,
  walk,
  masters = [],
  ledger = null,
  focus,
  lens,
  asOf,
  onOpen,
  onClearFocus,
  onJump,
  fund,
  canSetRecordStart = false,
  onSetRecordStart,
  contextual = false,
  chainAttention = null,
  summaryTarget = null,
}: Props) {
  // Ruling (Ale 2026-08-31): the fund area shows THE RECORD — proposed
  // objects appear only on the workbench.
  const chain = useMemo(() => chainOf(table.segments), [table.segments])
  const knownHead = useMemo(
    () => table.entities.find((e) => e.is_fund_root && e.state !== 'proposed'),
    [table.entities]
  )
  /* AS OF A DATE (R3b): the holdings the D view left out, keyed by their
   * leg, are drawn on the diagram greyed IN PLACE — so the officer sees
   * what the date excludes — but they are NOT on the chain: the prose, the
   * status counts and the focus model read the D view alone. */
  const ghostBy = useMemo(
    () =>
      new Map<string, DroppedLeg<RtSegment>>(
        (asOf?.droppedLegs ?? []).map((d) => [d.seg.position_id, d])
      ),
    [asOf]
  )
  const drawnChain = useMemo(
    () => (ghostBy.size ? [...chain, ...[...ghostBy.values()].map((d) => d.seg)] : chain),
    [chain, ghostBy]
  )
  const sources = useMemo(() => balanceSourcesOf(chain), [chain])
  /* The vertical section — recomputed from the current record on every
   * render, never stored. null = no focus, nothing dims. */
  const litIds = useMemo(
    () => sectionObjectIds(chain, focus, knownHead?.id),
    [chain, focus, knownHead]
  )
  const section = useMemo(() => (focus ? verticalSection(chain, focus) : []), [chain, focus])
  const lit = (objectId: string | null): Lit => {
    if (!litIds) return undefined
    return objectId && litIds.has(objectId) ? 'on' : 'dim'
  }
  const legLit = (s: RtSegment): Lit =>
    !focus ? undefined : segmentOnSection(section, s) ? 'on' : 'dim'
  const anyLit = (legs: RtSegment[]): Lit =>
    !focus ? undefined : legs.some((l) => legLit(l) === 'on') ? 'on' : 'dim'
  const walkByEntity = useMemo(() => new Map(walk.map((n) => [n.entity_id, n])), [walk])
  const entityById = useMemo(() => new Map(table.entities.map((e) => [e.id, e])), [table.entities])
  const instrumentById = useMemo(
    () => new Map(table.instruments.map((i) => [i.id, i])),
    [table.instruments]
  )
  /* The masters this feeder holds, keyed on the master's fund-head entity —
   * the issuer on the feeder's leg (16a). A leaf issuer in this map renders
   * as a master-fund box with its chain collapsed behind a door. */
  const masterByEntity = useMemo(() => new Map(masters.map((m) => [m.entity_id, m])), [masters])

  /* The fund's description — every figure a DATUM the record holds; the
   * sentence compiler formats it by the officer rules (K7 dates, 2dp). */
  const stats = useMemo(() => {
    const structure = overviewStructureFacts(table)
    /* TRADE COUNTS are POSITION trades (Ale 2026-09-03): an issuance or a
     * buy-back on the paper itself keeps its blue chip on the instrument
     * leg and is not a trade of the fund's record here. */
    const recordedTrades = table.trades.filter((t) => t.state !== 'proposed' && t.position_id)
    const dates = recordedTrades
      .map((t) => t.effective_date)
      .filter((d): d is string => !!d)
      .sort()
    const last = dates[dates.length - 1] ?? null
    return {
      ...structure,
      trades: recordedTrades.length,
      lastTrade: last,
    }
  }, [table])

  /* The chain-status numbers, bucketed once so they ADD UP (Ale
   * 2026-09-03: "37 vs 34 vs 32 is confusing"): a one-level fund shows
   * the headline alone; deeper funds show every part — the fund itself,
   * each level, elsewhere — and the parts sum to the headline. */
  const status = useMemo(
    () => chainStatus(chain, table.trades, matrix),
    [chain, table.trades, matrix]
  )
  /* The FEEDER chain status (16c): the masters on this chain, the feeder's
   * OWN demands (the master entities' own demands excluded — counted once,
   * in the master's figure), and every master counted IN FULL. The subjects
   * (the feeder itself + each master) sum to the headline. */
  const chainMasters = useMemo(() => mastersOnChain(chain, masters), [chain, masters])
  const isFeeder = chainMasters.length > 0
  const feeder = useMemo(() => {
    if (!isFeeder) return null
    const own = feederItself(
      matrix,
      chainMasters.map((m) => m.entity_id)
    )
    return feederChainStatus(own, chainMasters)
  }, [isFeeder, matrix, chainMasters])
  /* The headline figures: the LEDGER's when it is in (the one read every
   * screen counts from — the full record, never the lens); the client
   * bucketing's until then. */
  const overall = useMemo(
    () =>
      ledger
        ? {
            proven: ledger.owed.proven,
            total: ledger.owed.demanded,
            pending: ledger.owed.pending_signoff,
            owed: ledger.owed.total,
            optional: ledger.owed.optional,
            on_plugs: ledger.owed.on_plugs,
          }
        : { ...status.overall, on_plugs: 0 },
    [ledger, status]
  )
  /* The headline, split so the owed clause can be a door: everything up
   * to the owed datum, the clause from that datum to the sentence's end
   * (less the full stop), the full stop. With nothing owed there is no
   * door — the sentence renders whole. When some of the owed are demands
   * policy only recommends, one clause follows the door before the full
   * stop — "5 are still owed, 3 of them optional" (Ale 2026-09-04) — the
   * count itself never moves. */
  const headline = useMemo(() => {
    const c = compileSentence('fund.overview.chain_status', {
      proven: overall.proven,
      demanded: overall.total,
      owed: overall.owed,
    })
    const i = c.parts.findIndex((p) => p.kind === 'datum' && p.slot === 'owed')
    if (i < 0) return { text: c.text, head: c.parts, door: [], tail: '' }
    const door = c.parts.slice(i)
    const last = door[door.length - 1]
    let tail = ''
    if (last.kind === 'text' && last.text.endsWith('.')) {
      tail = '.'
      door[door.length - 1] = { ...last, text: last.text.slice(0, -1) }
    }
    return { text: c.text, head: c.parts.slice(0, i), door, tail }
  }, [overall])
  /* The optional clause after the door — "5 are still owed, 3 of them
   * optional" (Ale 2026-09-04) — the ledger's figure, the count itself
   * never moves. */
  const optionalClause = useMemo(
    () =>
      overall.optional > 0
        ? compileSentence('fund.overview.chain_status_optional', {
            optional: overall.optional,
          }).parts
        : [],
    [overall]
  )
  /* The optional demands themselves, for the "include optional" chip
   * (Ale 2026-09-05). The chain-status block IS the sign-off reading, and
   * a sign-off never waits on a recommended demand — so the chip changes
   * nothing but what is LISTED: the officer sees what he could still ask
   * for, and asks for it, before he signs. */
  const optionalRows = useMemo(
    () =>
      optionalDemands(flatEvidence(table, matrix)).map((d) => ({
        key: `${d.object.kind}:${d.object.id}:${d.code}`,
        code: d.code,
        words: d.words,
        object: d.object.name,
      })),
    [table, matrix]
  )
  const pct = overall.total ? Math.round((overall.proven / overall.total) * 100) : 0

  /* The graph: the chain as a TREE from the fund down, derived ONLY from
   * holder/issuer ids (F6 / hotfix #1252 — segment order once drew
   * Silvretta holding Tessin). Every ENTITY is drawn ONCE; edges are per
   * leg (F1): two instruments into one issuer are two leg columns merging
   * into one box. Siblings stand side by side on one bar. An entity
   * reached from two parents (a diamond) stands under the first; the
   * other parent's wires end in a pointer to it. Co-holders of an
   * instrument (other holders of the same paper) keep their side box,
   * on every leg. */
  const tree = useMemo(() => diagramTree(drawnChain, knownHead), [drawnChain, knownHead])
  /* A real DAG when any asset is co-owned (held by 2+ holders): the fan-bar
   * tree cannot draw a cross-wire, so the co-owned chain switches to the
   * DAG renderer, the only view (Ale 2026-09-03 dropped the grid). Where a
   * band's wires cross, that band is lengthened to de-crowd them. Single-
   * holder chains keep the fan-bar tree unchanged. */
  const coOwned = useMemo(() => hasCoOwnership(chain), [chain])
  /* A WIDE chain goes to the same canvas renderer (D4, 2026-09-04). The
   * fan-bar tree lays its siblings out in a flex row, so twelve target
   * assets stand in twelve full columns and run off the card (audit §10,
   * atlas 92). The canvas places every box itself: a wide level packs to
   * half a pitch and the columns that would then overlap take the row
   * below — their holder → instrument edge a drop longer, which is Ale's
   * de-crowding rule and never a grid. A FEEDER keeps the fan-bar tree
   * either way: its collapsed master shelf lives there. */
  const onCanvas = useMemo(
    () => coOwned || (masters.length === 0 && isWideChain(chain)),
    [coOwned, masters.length, chain]
  )
  const coHoldersOf = (s: RtSegment) =>
    s.instrument_id
      ? table.positions.filter(
          (p) =>
            p.instrument_id === s.instrument_id &&
            p.holder_name !== s.holder_name &&
            p.state !== 'proposed'
        )
      : []

  const entityBox = (
    entityId: string | null,
    name: string | null,
    key: string,
    depth: number,
    withWalkChip = false,
    ghost = false
  ) => {
    const e = entityId ? entityById.get(entityId) : null
    const node = entityId ? walkByEntity.get(entityId) : null
    const master = entityId ? masterByEntity.get(entityId) : null
    const state = lit(entityId)
    const meta = e
      ? [e.legal_form, e.jurisdiction].filter(Boolean).join(', ') ||
        e.entity_nature?.replace(/_/g, ' ')
      : null
    return (
      <div className="fa-stack" key={key}>
        <button
          type="button"
          className={`fa-nbox e${master ? ' master' : ''}${ghost ? ' ghost' : ''}${focusCls(state)}`}
          data-testid="graph-box"
          data-kind="entity"
          data-master={master ? true : undefined}
          data-ghost={ghost ? true : undefined}
          data-focus={state}
          data-entity={entityId ?? undefined}
          data-depth={depth}
          aria-pressed={state === 'on' ? true : undefined}
          onClick={() => entityId && onOpen({ type: 'entity', id: entityId })}
        >
          <div className="nm">{name ?? 'entity'}</div>
          {meta ? (
            <div className="meta" data-testid={master ? 'master-meta' : undefined}>
              {master ? `${meta} · master fund` : meta}
            </div>
          ) : null}
        </button>
        {master ? (
          /* 16a: the master's chain stays COLLAPSED — its own headline as a
             grey shelf, the door leaving the feeder for the master's own
             record. Never expanded inline. */
          <div className="fa-master" data-testid="master-collapsed">
            <div className="fa-master-shelf" data-testid="master-headline">
              its own chain: {master.levels} level{master.levels === 1 ? '' : 's'}, {master.targets}{' '}
              target asset{master.targets === 1 ? '' : 's'} · {master.proven} of {master.total}{' '}
              proven
            </div>
            <Link
              className="fa-walkbtn"
              to={`/v2/funds/${master.fund_id}?tab=record`}
              data-testid="master-door"
            >
              master fund — open its record ›
            </Link>
          </div>
        ) : withWalkChip && node ? (
          <WalkChip node={node} fundId={fundId} />
        ) : null}
      </div>
    )
  }

  /* The instrument box carries its issued balance as a small second line
   * — a count of the paper, never a currency (Ale 2026-09-03). */
  const instrumentBox = (
    instrumentId: string | null,
    name: string | null,
    key: string,
    ghost = false
  ) => {
    const i = instrumentId ? instrumentById.get(instrumentId) : null
    const issued = i?.outstanding_units ?? null
    const state = lit(instrumentId)
    return (
      <button
        type="button"
        className={`fa-nbox i${ghost ? ' ghost' : ''}${focusCls(state)}`}
        key={key}
        data-testid="graph-box"
        data-kind="instrument"
        data-instrument={instrumentId ?? undefined}
        data-ghost={ghost ? true : undefined}
        data-focus={state}
        aria-pressed={state === 'on' ? true : undefined}
        onClick={() => instrumentId && onOpen({ type: 'instrument', id: instrumentId })}
      >
        <div className="nm">{name ?? 'instrument'}</div>
        {i ? (
          issued !== null ? (
            <div className="meta ibal" data-testid="instrument-issued">
              {fmt(issued)} issued
            </div>
          ) : (
            <div className="meta ns" data-testid="instrument-issued">
              issued amount not stated
            </div>
          )
        ) : null}
      </button>
    )
  }

  /* Trades on the POSITION — every record of the leg (a commitment package
   * moves on both its records). */
  const tradesOf = (s: RtSegment) =>
    table.trades.filter(
      (t) =>
        t.position_id && (s.position_ids.includes(t.position_id) || t.position_id === s.position_id)
    ).length

  /** What the chip on a leg says: the trade kind when they are all of one
   *  kind ("1 issuance", "2 capital calls"), else plain "trades". */
  const tradeWords = (kinds: string[]) => {
    const n = kinds.length
    const one = kinds.every((k) => k === kinds[0]) ? kinds[0].replace(/_/g, ' ') : 'trade'
    return `${n} ${one}${n === 1 ? '' : 's'}`
  }

  /* The POSITION leg: the long amber wire from the holder into the
   * instrument, its figures beside it on a light shade, its trades as an
   * amber chip. `top` says how it joins the holder's fan bar. */
  const posEdge = (s: RtSegment, top: End, bottom: End = 'none') => {
    const tradeCount = tradesOf(s)
    const state = legLit(s)
    const ghost = ghostBy.get(s.position_id)
    if (ghost && asOf) {
      /* No balance as of the date (R3b): the leg stays where it belongs,
       * greyed and dashed, saying so — and when its evidence begins. The
       * trades chip leaves the date view: it goes back to today, focused
       * on the holding. */
      const first = formatOfficerDate(ghost.firstEvidence)
      return (
        <Wire
          kind="pos"
          top={top}
          bottom={bottom}
          arrow={bottom === 'none'}
          height={POS_H}
          lit={state}
          ghost
          testid="graph-edge"
        >
          <div className="fa-fig" data-testid="chain-leg-nobalance" data-position={s.position_id}>
            <span className="fa-shade ghost">no balance as of {formatOfficerDate(asOf.date)}</span>
            <span className="fa-dateline" data-testid="chain-leg-first-evidence">
              {first ? `first evidence ${first}` : 'no dated evidence on record'}
            </span>
            {ghost.tradesAfter > 0 ? (
              <button
                type="button"
                className="fa-tchip ghost"
                onClick={() => {
                  asOf.onBackToday()
                  onJump({ type: 'position', id: s.position_id })
                }}
                data-testid="ov-trade-chip-after"
              >
                {ghost.tradesAfter} trade{ghost.tradesAfter === 1 ? '' : 's'} after this date ›
              </button>
            ) : null}
          </div>
        </Wire>
      )
    }
    const dateLine = asOf ? sourceLine(s) : null
    return (
      <Wire
        kind="pos"
        top={top}
        bottom={bottom}
        arrow={bottom === 'none'}
        height={POS_H}
        lit={state}
        testid="graph-edge"
      >
        <div className="fa-fig" data-testid="graph-position">
          <span className="fa-shade pos">{positionFigures(s)}</span>
          {dateLine ? (
            <span
              className="fa-dateline"
              data-testid="chain-leg-asof"
              data-source={s.balance_source ?? undefined}
            >
              {dateLine}
            </span>
          ) : null}
          {tradeCount > 0 ? (
            <button
              type="button"
              className="fa-tchip pos"
              onClick={() => onJump({ type: 'position', id: s.position_id })}
              data-testid="ov-trade-chip"
            >
              {tradeCount} trade{tradeCount === 1 ? '' : 's'} ›
            </button>
          ) : null}
        </div>
      </Wire>
    )
  }

  /* The INSTRUMENT leg: the short blue wire from the paper into its
   * issuer. `bottom` says how it joins a merge bar (two papers into one
   * issuer); alone, it ends in an arrowhead. Trades on the paper itself
   * (an issuance, a buy-back) are a blue chip beside it. */
  const issuedEdge = (s: RtSegment, bottom: End, height = INS_H) => {
    const ghost = ghostBy.has(s.position_id)
    const trades = ghost ? [] : instrumentTradesOf(table.trades, s.instrument_id)
    return (
      <Wire
        kind="ins"
        top="none"
        bottom={bottom}
        arrow={bottom === 'none'}
        height={height}
        lit={legLit(s)}
        ghost={ghost}
        testid="graph-edge"
      >
        {trades.length > 0 && s.instrument_id ? (
          <div className="fa-fig">
            <button
              type="button"
              className="fa-tchip ins"
              onClick={() => onJump({ type: 'instrument', id: s.instrument_id as string })}
              data-testid="ov-instrument-trade-chip"
            >
              {tradeWords(trades.map((t) => t.transaction_type))} ›
            </button>
          </div>
        ) : null}
      </Wire>
    )
  }

  /* A co-holder's box is never on a section (it is beside the chain, not
   * above the focused object), so with a focus set it dims like the rest. */
  const asideLit: Lit = focus ? 'dim' : undefined

  /* One LEG: position wire → instrument box → instrument wire. Other
   * holders of the same instrument stand beside the position wire, their
   * own amber wires merging with it on a bar into the paper. */
  const legEdges = (s: RtSegment, top: End, bottom: End, insHeight: number) => {
    const co = coHoldersOf(s)
    return (
      <>
        {co.length ? (
          <>
            <div className="fa-cols">
              <div className="fa-col">{posEdge(s, top, 'first')}</div>
              {co.map((p, i) => (
                <div className="fa-col" key={p.id}>
                  <button
                    type="button"
                    className={`fa-nbox e${focusCls(asideLit)}`}
                    data-testid="graph-coholder"
                    data-kind="entity"
                    data-focus={asideLit}
                    onClick={() => onJump({ type: 'position', id: p.id })}
                  >
                    <div className="nm">{p.holder_name}</div>
                  </button>
                  <Wire
                    kind="pos"
                    top="none"
                    bottom={i === co.length - 1 ? 'last' : 'mid'}
                    height={POS_H}
                    lit={asideLit}
                  >
                    <div className="fa-fig">
                      <span className="fa-shade pos">{fmt(p.units_held, p.units_currency)}</span>
                    </div>
                  </Wire>
                </div>
              ))}
            </div>
            <Stub kind="pos" arrow lit={legLit(s)} />
          </>
        ) : (
          posEdge(s, top)
        )}
        {instrumentBox(
          s.instrument_id,
          s.instrument_name,
          `i-${s.position_id}`,
          ghostBy.has(s.position_id)
        )}
        {issuedEdge(s, bottom, insHeight)}
      </>
    )
  }

  /* One CHILD of a holder: its legs (one column each, merging on a bar
   * when there are several), then the issuer drawn once, then whatever
   * the issuer holds. The walk chip sits on chain bottoms only. `joins`
   * is the holder's fan — where each leg meets the bar above. A merged
   * child is taller by its stub, so single-leg siblings in the same row
   * stretch their instrument wire by as much and every issuer box in the
   * row stands level. */
  const renderChild = (
    c: TreeChild<RtSegment>,
    parentDepth: number,
    joins: Map<string, Join>,
    stretch = 0
  ): React.ReactNode => {
    const key = c.legs[0].position_id
    const issuerNode = c.node
    const leaf = !issuerNode || issuerNode.children.length === 0
    const depth = issuerNode?.depth ?? parentDepth + 1
    const merged = c.legs.length > 1
    const merge = mergeJoins(c)
    // An issuer only the greyed legs reach is greyed with them.
    const allGhost = c.legs.every((l) => ghostBy.has(l.position_id))
    return (
      <div
        className="fa-grp"
        key={key}
        data-testid="graph-node"
        data-entity={c.issuerId ?? undefined}
        data-legs={c.legs.length}
        style={{ gridColumn: 'span ' + c.legs.length }}
      >
        <div className="fa-cols">
          {c.legs.map((l) => (
            <div className="fa-col" key={l.position_id} data-testid="graph-leg">
              {legEdges(
                l,
                joins.get(l.position_id) ?? 'only',
                merged ? (merge.get(l.position_id) ?? 'only') : 'none',
                merged ? INS_H : INS_H + stretch
              )}
            </div>
          ))}
        </div>
        {merged ? (
          <Stub kind="ins" arrow lit={anyLit(c.legs)} ghost={allGhost} testid="graph-merge" />
        ) : null}
        {c.drawnUnder ? (
          /* A co-investment: the issuer's box stands under the parent that
           * reached it first; this parent's wires end in a pointer to it. */
          <div
            className={`fa-tstate t-rule${focusCls(anyLit(c.legs))}`}
            data-testid="graph-ref"
            data-entity={c.issuerId ?? undefined}
          >
            {c.issuerName ?? 'entity'} — drawn once,{' '}
            {c.drawnUnder.name ? `under ${c.drawnUnder.name}` : 'above'}
          </div>
        ) : (
          <>
            {entityBox(
              c.issuerId,
              c.issuerName,
              `e-${c.issuerId ?? key}-${key}`,
              depth,
              leaf,
              allGhost
            )}
            {issuerNode ? renderChildren(issuerNode) : null}
          </>
        )}
      </div>
    )
  }

  /* What a holder holds: its legs fan out from one amber stub onto one
   * bar, every leg a column — the two ends of the bar curve down into
   * their wires, the middle ones pass through. A lone leg is a straight
   * wire with no bar. */
  const renderChildren = (node: TreeNode<RtSegment>): React.ReactNode => {
    if (node.children.length === 0) return null
    const joins = fanJoins(node)
    const legs = node.children.flatMap((c) => c.legs)
    const fan = legs.length > 1
    const stretch = node.children.some((c) => c.legs.length > 1) ? MERGE_H : 0
    return (
      <>
        {fan ? <Stub kind="pos" lit={anyLit(legs)} testid="graph-fan" /> : null}
        <div
          className="fa-cols fa-fan-row"
          style={{ gridTemplateColumns: 'repeat(' + legs.length + ', minmax(228px, 1fr))' }}
          data-testid={node.children.length > 1 ? 'graph-siblings' : undefined}
        >
          {node.children.map((c) => renderChild(c, node.depth, joins, stretch))}
        </div>
      </>
    )
  }

  const levelVerb = (holders: string[]) => (holders.length === 1 ? 'holds' : 'hold')
  /* The holder as the level line names it — its first word, the way the
   * officer says it ("Nerine holds …", Ale 2026-09-03). */
  const holderWord = (name: string) => name.split(' ')[0]

  const prose = (
    <div className="fa-prose">
      {asOf ? (
        <button
          type="button"
          className="fa-tlink fa-prose-door"
          onClick={asOf.onBackToday}
          data-testid="rebuild-back-today"
        >
          back to today ›
        </button>
      ) : null}
      <p style={{ margin: 0, display: 'inline' }} data-testid="fund-stat-strip">
        {asOf ? (
          /* AS OF A DATE: the same shape, spoken of that date — and one
                 sentence per holding the date excludes, with the date its
                 evidence begins. */
          <>
            <Sentence
              meaning="fund.overview.asof.chain"
              values={{
                fund: table.fund_name,
                date: asOf.date,
                levels: stats.levels,
                targets: stats.targets,
                instruments: stats.instruments,
              }}
              data-testid="fund-prose-chain"
            />{' '}
            <Sentence
              meaning="fund.overview.asof.activity"
              values={{ trades: stats.trades, last_trade: stats.lastTrade }}
              data-testid="fund-prose-activity"
            />
            {asOf.droppedLegs.map((d) => (
              <Fragment key={d.seg.position_id}>
                {' '}
                <Sentence
                  meaning="fund.overview.asof.nobalance"
                  values={{ holding: d.seg.instrument_name, first: d.firstEvidence }}
                  data-testid="fund-prose-nobalance"
                />
              </Fragment>
            ))}
          </>
        ) : (
          <>
            <Sentence
              meaning={
                stats.classes && stats.classes.length
                  ? 'fund.overview.classes'
                  : 'fund.overview.classes.none'
              }
              values={{ fund: table.fund_name, classes: stats.classes }}
              data-testid="fund-prose-classes"
            />{' '}
            <Sentence
              meaning="fund.overview.chain"
              values={{
                levels: stats.levels,
                targets: stats.targets,
                instruments: stats.instruments,
              }}
              data-testid="fund-prose-chain"
            />{' '}
            <Sentence
              meaning="fund.overview.activity"
              values={{
                trades: stats.trades,
                last_trade: stats.lastTrade,
              }}
              data-testid="fund-prose-activity"
            />
          </>
        )}
      </p>{' '}
      <HelpDot k="canon.datum" />
    </div>
  )

  return (
    <div data-testid="fund-overview">
      <FundSection
        title="The chain"
        collapsible={contextual}
        defaultOpen
        attention={chainAttention}
        revealKey={focus ? JSON.stringify(focus) : null}
        actions={
          <>
            {/* The lens pill and the door home sit LEFT of the diagram, so
              the header's calendar popover never hides them (R3b). */}
            {lens ? (
              <span className="fa-lens" data-testid="rebuild-lens">
                {lens.label}
              </span>
            ) : null}
            {asOf ? (
              <button
                type="button"
                className="fa-tlink"
                onClick={asOf.onBackToday}
                data-testid="rebuild-back-today"
              >
                back to today ›
              </button>
            ) : null}
            {focus ? (
              <FocusChip table={table} focus={focus} onClear={onClearFocus} />
            ) : (
              // Reserve the same baseline/height before selection so the chart
              // does not move when its focus chip appears. No hidden control.
              <span className="fa-focuschip" aria-hidden="true" style={{ visibility: 'hidden' }}>
                &nbsp;
              </span>
            )}
            <span className="right">
              <HelpDot k="fund.chain" />
            </span>
          </>
        }
      >
        <div className="fa-chart" data-testid="fund-chain-graph">
          {!tree ? (
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--fa-faint)', padding: 12 }}>
              No chain on record yet — the record builds as documents book.
            </div>
          ) : (
            <ChainCanvas>
              {onCanvas ? (
                <CoOwnedChain
                  fundId={fundId}
                  table={table}
                  chain={chain}
                  walk={walk}
                  litIds={litIds}
                  section={section}
                  hasFocus={!!focus}
                  onOpen={onOpen}
                  onJump={onJump}
                />
              ) : (
                <div className="fa-stack">
                  {entityBox(tree.entityId, tree.name, 'h0', 0, tree.children.length === 0)}
                  {renderChildren(tree)}
                </div>
              )}
            </ChainCanvas>
          )}
        </div>
        {/* SENTENCES, NOT LABELS (Ale 2026-09-02): the fund description is
            prose carrying the figures — each figure a datum the record
            holds — instead of a strip of bare numbers. */}
        {summaryTarget ? createPortal(prose, summaryTarget) : prose}
      </FundSection>

      {/* Chain status as SENTENCES (Ale 2026-09-03): the headline counts
          demands proven against demands made — a waiver with its reason
          counts as proven — and each level line names ALL its issuers.
          16c: a FEEDER counts its masters IN FULL — the feeder itself plus
          one subject per master (its own headline verbatim), summing to the
          headline; each master figure is a door to that master's record. */}
      {!contextual ? (
        isFeeder && feeder ? (
          <div className="fa-card" data-testid="feeder-status-card">
            <div className="fa-chd">
              Chain status — how much is proven
              <span className="right">
                {lens ? (
                  <span className="fa-lens" data-testid="rebuild-lens">
                    {lens.label}
                  </span>
                ) : null}
                <HelpDot k="fund.chain_status" />
              </span>
            </div>
            <div style={{ padding: '12px 16px 4px' }}>
              <div className="fa-status" data-testid="feeder-chain-status">
                <span className="sn">
                  <b className="sn-datum">{feeder.overall.proven}</b> of{' '}
                  <b className="sn-datum">{feeder.overall.total}</b> evidence demands on everything
                  this fund reaches are proven
                  {feeder.overall.owed > 0 ? (
                    <>
                      ;{' '}
                      <Link
                        className="fa-owed-door"
                        to={`/v2/funds/${table.fund_id}?tab=evidence&show=owed`}
                        data-testid="owed-door"
                      >
                        <b className="sn-datum">{feeder.overall.owed}</b>{' '}
                        {feeder.overall.owed === 1 ? 'is' : 'are'} still owed
                      </Link>
                      .
                    </>
                  ) : (
                    '.'
                  )}
                </span>
              </div>
              <div className="fa-evbar">
                <i
                  style={{
                    width: `${
                      feeder.overall.total
                        ? Math.round((feeder.overall.proven / feeder.overall.total) * 100)
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
            <div style={{ padding: '8px 0 4px' }}>
              <div
                className={`fa-evrow ${feeder.feeder.proven >= feeder.feeder.total ? 'full' : 'part'}`}
                data-testid="feeder-status-part"
                data-part="feeder"
              >
                On the feeder itself: <b className="sn-datum">{feeder.feeder.proven}</b> of{' '}
                <b className="sn-datum">{feeder.feeder.total}</b>.
              </div>
              {(() => {
                const mt = mastersTotal(feeder)
                return (
                  <div
                    className={`fa-evrow ${mt.proven >= mt.total ? 'full' : 'part'}`}
                    data-testid="feeder-status-part"
                    data-part="masters"
                  >
                    Through {joinNames(feeder.masters.map((m) => m.link.name))}:{' '}
                    <b className="sn-datum">{mt.proven}</b> of{' '}
                    <b className="sn-datum">{mt.total}</b> —{' '}
                    {feeder.masters.map((m, i) => (
                      <Fragment key={m.link.entity_id}>
                        {i > 0 ? ', ' : ''}
                        <Link
                          className="fa-tlink"
                          to={`/v2/funds/${m.link.fund_id}?tab=record`}
                          data-testid="master-figure-door"
                          data-fund={m.link.fund_id}
                        >
                          <b className="sn-datum">{m.proven}</b> of{' '}
                          <b className="sn-datum">{m.total}</b> on {m.link.name} ›
                        </Link>
                      </Fragment>
                    ))}
                    . Each master is counted in full.
                  </div>
                )
              })()}
            </div>
          </div>
        ) : (
          <div className="fa-card">
            <div className="fa-chd">
              Chain status — how much is proven
              {lens ? (
                <span className="fa-lens" data-testid="rebuild-lens">
                  {lens.label}
                </span>
              ) : null}
              <span className="right">
                <HelpDot k="fund.chain_status" />
              </span>
            </div>
            <div style={{ padding: '12px 16px 4px' }}>
              {asOf ? (
                /* Where the balances of the date come from (R3b): the
                 statements they rest on, the legs fixed by movements alone,
                 the holdings without a balance yet. The counts follow what
                 the diagram draws, so the numbers add up. */
                <div className="fa-asof-src" data-testid="asof-balance-sources">
                  Balances as of <b className="sn-datum">{formatOfficerDate(asOf.date)}</b>:{' '}
                  {sources.statementDates.length ? (
                    <>
                      from statements dated{' '}
                      {sources.statementDates.map((d, i) => (
                        <Fragment key={d}>
                          {i > 0 ? ', ' : ''}
                          <b className="sn-datum">{formatOfficerDate(d)}</b>
                        </Fragment>
                      ))}
                    </>
                  ) : (
                    'no statement dated on or before that date'
                  )}
                  {sources.fromMovements.length ? (
                    <>
                      {' '}
                      · <b className="sn-datum">{sources.fromMovements.length}</b>{' '}
                      {sources.fromMovements.length === 1 ? 'position' : 'positions'} from movements
                      only (
                      {joinNames(
                        sources.fromMovements.map((s) => s.instrument_name ?? 'a holding')
                      )}
                      )
                    </>
                  ) : null}
                  {asOf.droppedLegs.length ? (
                    <>
                      {' '}
                      · <b className="sn-datum">{asOf.droppedLegs.length}</b>{' '}
                      {asOf.droppedLegs.length === 1 ? 'holding' : 'holdings'} without a balance (
                      {asOf.droppedLegs.map((d, i) => (
                        <Fragment key={d.seg.position_id}>
                          {i > 0 ? '; ' : ''}
                          {d.seg.instrument_name ?? 'a holding'}
                          {d.firstEvidence ? (
                            <>
                              , first evidence{' '}
                              <b className="sn-datum">{formatOfficerDate(d.firstEvidence)}</b>
                            </>
                          ) : null}
                        </Fragment>
                      ))}
                      )
                    </>
                  ) : null}
                  .
                </div>
              ) : null}
              <div className="fa-status" data-testid="fund-chain-status">
                <span
                  className="sn"
                  data-meaning="fund.overview.chain_status"
                  title={headline.text}
                  data-testid="fund-chain-status-sentence"
                >
                  <SentenceParts parts={headline.head} />
                  {headline.door.length ? (
                    /* "3 are still owed" IS the door (Ale 2026-09-03): the
                   Evidence tab, filtered to what is owed. */
                    <Link
                      className="fa-owed-door"
                      to={`/v2/funds/${table.fund_id}?tab=evidence&show=owed`}
                      data-testid="owed-door"
                    >
                      <SentenceParts parts={headline.door} />
                    </Link>
                  ) : null}
                  {headline.door.length && optionalClause.length ? (
                    <span className="fa-opt" data-testid="owed-optional">
                      <SentenceParts parts={optionalClause} />
                    </span>
                  ) : null}
                </span>
                {headline.door.length && overall.on_plugs > 0 ? (
                  /* The owed rows anchored to a plug trade, the ledger's own
                   * words — never chased until the break is approved. */
                  <span className="fa-owed-split" data-testid="owed-split">
                    , {overall.on_plugs} on {overall.on_plugs === 1 ? 'a plug' : 'plugs'}
                  </span>
                ) : null}
                {headline.tail ? <span>{headline.tail}</span> : null}
                {overall.pending > 0 ? (
                  <>
                    {' '}
                    <Sentence
                      meaning="fund.overview.chain_status_pending"
                      values={{ pending: overall.pending }}
                      data-testid="fund-chain-status-pending"
                    />
                  </>
                ) : null}
                {ledger && ledger.breaks.open > 0 ? (
                  /* The standing breaks, a door to the reconciliation queue —
                   * the same figure the Record banner and the queue print. */
                  <>
                    {' '}
                    <Link
                      className="fa-owed-door"
                      to={`/v2/workbench/reconciliation?fund=${table.fund_id}`}
                      data-testid="breaks-door"
                    >
                      {breaksWords(ledger.breaks)} to reconcile
                    </Link>
                    .
                  </>
                ) : null}
              </div>
              <div className="fa-evbar">
                <i style={{ width: `${pct}%` }} />
              </div>
              {asOf && asOf.notes.evidence_as_of === 'current' ? (
                /* The matrix carries no document dates yet: the proof status
                 is today's, and the demands on what the date excludes sit
                 outside its denominator — named, with a door to them. */
                <div className="fa-asof-note" data-testid="asof-evidence-current">
                  proof status as it stands today
                  {asOf.outside.count > 0 ? (
                    <>
                      {' '}
                      ·{' '}
                      <Link
                        className="fa-tlink"
                        to={`/v2/funds/${table.fund_id}?tab=evidence`}
                        data-testid="asof-outside-door"
                      >
                        <b className="sn-datum">{asOf.outside.count}</b>{' '}
                        {asOf.outside.count === 1 ? 'demand' : 'demands'}
                        {asOf.outside.names.length
                          ? ` on ${joinNames(asOf.outside.names)}`
                          : ''}{' '}
                        {asOf.outside.count === 1 ? 'sits' : 'sit'} outside this date ›
                      </Link>
                    </>
                  ) : null}
                </div>
              ) : null}
              {/* "include optional" — the same switch the Evidence tab and
                the sign-off card read. OFF by default; the sign-off is
                unaffected either way. */}
              <div className="fa-optbar">
                <OptionalChip n={overall.optional} testId="chain-optional-toggle" />
              </div>
              <OptionalList
                demands={optionalRows}
                fundId={table.fund_id}
                testId="chain-optional-list"
              />
            </div>
            <div style={{ padding: '8px 0 4px' }}>
              {showParts(status) ? (
                <>
                  {status.fund.total > 0 ? (
                    <div
                      className={`fa-evrow ${status.fund.proven >= status.fund.total ? 'full' : 'part'}`}
                      data-testid="fund-status-part"
                      data-part="fund"
                    >
                      <Sentence
                        meaning="fund.overview.fund_itself"
                        values={{ proven: status.fund.proven, demanded: status.fund.total }}
                      />
                    </div>
                  ) : null}
                  {status.levels.map((l) => (
                    <div
                      className={`fa-evrow ${l.proven >= l.total ? 'full' : 'part'}`}
                      key={l.depth}
                      data-testid="fund-status-part"
                      data-part="level"
                      data-depth={l.depth}
                    >
                      <Sentence
                        meaning="fund.overview.level"
                        values={{
                          level: l.depth,
                          holders: l.holders.map(holderWord),
                          verb: levelVerb(l.holders),
                          issuers: shortNames(l.issuers),
                          proven: l.proven,
                          demanded: l.total,
                        }}
                        data-testid="fund-level-sentence"
                      />
                    </div>
                  ))}
                  {status.elsewhere.total > 0 ? (
                    <div
                      className={`fa-evrow ${status.elsewhere.proven >= status.elsewhere.total ? 'full' : 'part'}`}
                      data-testid="fund-status-part"
                      data-part="elsewhere"
                    >
                      <Sentence
                        meaning="fund.overview.elsewhere"
                        values={{
                          proven: status.elsewhere.proven,
                          demanded: status.elsewhere.total,
                        }}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        )
      ) : null}

      {/* The fund facts (Ale 2026-09-04): the record start, inline for a
          manager — nothing dated before it is demanded or chased. */}
      {!contextual ? (
        <FundFacts fund={fund} canSet={canSetRecordStart} onSet={onSetRecordStart} />
      ) : null}
    </div>
  )
}
